import { useState } from 'react'
import { triggerGovernanceScan } from '../services/supabase'
import { PlayCircle, Loader2, CheckCircle, XCircle } from 'lucide-react'
import clsx from 'clsx'

interface ScanHistory {
  id: string
  app_name: string
  mode: string
  status: string
  compliance_score: number
  violations: number
  timestamp: string
}

export default function Scans() {
  const [scanning, setScanning] = useState(false)
  const [selectedApp, setSelectedApp] = useState('intelligence')
  const [selectedMode, setSelectedMode] = useState<'quick' | 'deep'>('quick')
  const [scanResult, setScanResult] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)

  const apps = [
    'intelligence',
    'assessment',
    'benchmark',
    'framework',
  ]

  const handleScan = async () => {
    setScanning(true)
    setError(null)
    setScanResult(null)

    try {
      const result = await triggerGovernanceScan(selectedApp, selectedMode)
      setScanResult(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scan failed')
    } finally {
      setScanning(false)
    }
  }

  // Mock scan history
  const scanHistory: ScanHistory[] = [
    {
      id: '1',
      app_name: 'intelligence',
      mode: 'quick',
      status: 'complete',
      compliance_score: 0.774,
      violations: 21,
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: '2',
      app_name: 'assessment',
      mode: 'deep',
      status: 'complete',
      compliance_score: 0.92,
      violations: 3,
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: '3',
      app_name: 'benchmark',
      mode: 'quick',
      status: 'complete',
      compliance_score: 0.88,
      violations: 7,
      timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Compliance Scans</h2>
        <p className="mt-1 text-sm text-gray-500">
          Trigger and monitor FMS Contract compliance scans across applications
        </p>
      </div>

      {/* Scan Trigger Panel */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Trigger New Scan
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Application
            </label>
            <select
              value={selectedApp}
              onChange={(e) => setSelectedApp(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dii-primary"
              disabled={scanning}
            >
              {apps.map((app) => (
                <option key={app} value={app}>
                  {app.charAt(0).toUpperCase() + app.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Scan Mode
            </label>
            <select
              value={selectedMode}
              onChange={(e) => setSelectedMode(e.target.value as 'quick' | 'deep')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dii-primary"
              disabled={scanning}
            >
              <option value="quick">Quick Scan (~5s)</option>
              <option value="deep">Deep Scan (~30s)</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={handleScan}
              disabled={scanning}
              className={clsx(
                'w-full px-4 py-2 rounded-md font-medium flex items-center justify-center',
                scanning
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-dii-primary text-white hover:bg-blue-600'
              )}
            >
              {scanning ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <PlayCircle className="h-5 w-5 mr-2" />
                  Start Scan
                </>
              )}
            </button>
          </div>
        </div>

        {/* Scan Result */}
        {scanResult && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <div className="ml-3">
                <h4 className="text-sm font-medium text-green-900">
                  Scan Completed Successfully
                </h4>
                <div className="mt-2 text-sm text-green-800">
                  <p>Scan ID: {scanResult.scan_id}</p>
                  <p>Compliance Score: {Math.round(scanResult.compliance_score * 100)}%</p>
                  <p>Violations Found: {scanResult.violations?.length || 0}</p>
                  <p>Components Scanned: {scanResult.components_scanned || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
              <div className="ml-3">
                <h4 className="text-sm font-medium text-red-900">
                  Scan Failed
                </h4>
                <p className="mt-1 text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scan History */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Recent Scan History
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Application
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mode
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Compliance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Violations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {scanHistory.map((scan) => (
                <tr key={scan.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 capitalize">
                      {scan.app_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={clsx(
                        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                        scan.mode === 'quick'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      )}
                    >
                      {scan.mode}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      {scan.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span
                        className={clsx(
                          'text-sm font-medium',
                          scan.compliance_score >= 0.9
                            ? 'text-green-600'
                            : scan.compliance_score >= 0.7
                            ? 'text-yellow-600'
                            : 'text-red-600'
                        )}
                      >
                        {Math.round(scan.compliance_score * 100)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={clsx(
                        'text-sm font-medium',
                        scan.violations > 0 ? 'text-red-600' : 'text-green-600'
                      )}
                    >
                      {scan.violations}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(scan.timestamp).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}